import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { StockItemSuppliersQuery, StockItemSuppliersQueryParameter } from "../shared/models/customerBuyerStockItemsModels";
import { CustomerProductSuppliersState, SupplierFiltersQuery } from "./models/models";
import customerBuyerStockItemsApi from "../shared/api/customerBuyerStockItemsApi";
import customerCompaniesApi from "../companies/api/customerCompaniesApi";

export const getProduct = createAsyncThunk(
  "customerProductSuppliers/getProduct",
  async (productId: string) => {
    return customerBuyerStockItemsApi.getGroupedStockItem(productId);
  }
);

export const getSuppliers = createAsyncThunk(
  "customerProductSuppliers/getSuppliers",
  async ({ productId, query }: { productId: string, query: StockItemSuppliersQuery }) => {
    return customerBuyerStockItemsApi.getStockItemSuppliers(productId, query);
  }
);

export const getSuppliersFilters = createAsyncThunk(
  "customerProductSuppliers/getSuppliersFilters",
  async ({ productId, query }: { productId: string, query: SupplierFiltersQuery }) => {
    return customerBuyerStockItemsApi.getStockItemSuppliersFilters(productId, query);
  }
);

export const getSupplierProductVariants = createAsyncThunk(
  "customerProductSuppliers/getSupplierProductVariants",
  async ({ productId, supplierId }: { productId: string, supplierId: string }) => {
    return customerBuyerStockItemsApi.getStockItemSupplierDetails(productId, supplierId);
  }
);

export const getCompany = createAsyncThunk(
  "customerProductSuppliers/getCompany",
  async (id: string) => {
    return customerCompaniesApi.getCompany(id);
  }
);

export const getCustomerShippingOptions = createAsyncThunk(
  "customerProductSuppliers/getCustomerShippingOptions",
  async (id: string) => {
    return customerCompaniesApi.getShippingOptions(id);
  }
);

const initialState: CustomerProductSuppliersState = {
  query: {
    sortBy: StockItemSuppliersQueryParameter.name,
    sortDesc: false,
    pageNo: 1,
    pageSize: 20,
    minPrice: undefined,
    maxPrice: 0,
    countryIds: [],
    deliveryCodes: [],
    conditionCodes: [],
    groupIds: []
  },
  product: undefined,
  productLoading: false,
  suppliers: undefined,
  supplier: undefined,
  filters: {
    minPrice: 0,
    maxPrice: 0,
    countries: [],
    conditions: [],
    delivery: [],
    groups: []
  },
  filtersQuery: {
    minPrice: 0,
    maxPrice: 0,
    conditionCodes: [],
    countryIds: [],
    deliveryCodes: [],
    groupIds: []
  },
  loading: false,
  supplierDetails: undefined,
  suppliersLoading: false,
  supplierDetailsLoading: false,
  shipping: undefined,
  company: undefined,
  success: undefined,
  error: undefined
};

const customerProductSuppliersSlice = createSlice({
  name: "customerProductSuppliers",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    changeProductSupplierFiltersQuery: (state, action) => {
      state.filtersQuery = action.payload;
    },
    clearSuppliers: (state, action) => {
      state.suppliers = undefined;
    },
    clearSupplierItems: (state, action) => {
      if (state.suppliers) {
        state.suppliers.items = [];
        state.suppliers.pageNo = 0;
      }
    },
    selectSupplier: (state, { payload }) => {
      state.supplier = payload;
    },
    setSupplierDetails: (state, { payload }) => {
      state.supplierDetails = payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getProduct.pending, (state) => {
        state.productLoading = true;
      })
      .addCase(getProduct.fulfilled, (state, { payload }) => {
        state.product = payload;
        state.productLoading = false;
      })
      .addCase(getProduct.rejected, (state, { error }) => {
        state.error = error;
        state.productLoading = false;
      });

    builder
      .addCase(getSuppliers.pending, (state) => {
        state.suppliersLoading = true;
      })
      .addCase(getSuppliers.fulfilled, (state, { payload }) => {
        if (!state.suppliers) {
          state.suppliers = payload;
        } else if (state.suppliers.pageNo !== payload.pageNo) {
          state.suppliers = { ...payload, items: state.suppliers.items.concat(payload.items) };
        }

        state.suppliersLoading = false;
      })
      .addCase(getSuppliers.rejected, (state, { error }) => {
        state.error = error;
        state.suppliersLoading = false;
      });

    builder
      .addCase(getSuppliersFilters.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSuppliersFilters.fulfilled, (state, { payload }) => {
        state.filters = payload;
        state.loading = false;
      })
      .addCase(getSuppliersFilters.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getSupplierProductVariants.pending, (state) => {
        state.supplierDetailsLoading = true;
      })
      .addCase(getSupplierProductVariants.fulfilled, (state, { payload }) => {
        state.supplierDetails = payload;
        state.supplierDetailsLoading = false;
      })
      .addCase(getSupplierProductVariants.rejected, (state, { error }) => {
        state.error = error;
        state.supplierDetailsLoading = false;
      });

    builder
      .addCase(getCustomerShippingOptions.pending, (state) => {
        state.supplierDetailsLoading = true;
      })
      .addCase(getCustomerShippingOptions.fulfilled, (state, { payload }) => {
        state.shipping = payload;
        state.supplierDetailsLoading = false;
      })
      .addCase(getCustomerShippingOptions.rejected, (state, { error }) => {
        state.error = error;
        state.supplierDetailsLoading = false;
      });  

    builder
      .addCase(getCompany.pending, (state) => {
        state.supplierDetailsLoading = true;
      })
      .addCase(getCompany.fulfilled, (state, { payload }) => {
        state.company = payload;
        state.supplierDetailsLoading = false;
      })
      .addCase(getCompany.rejected, (state, { error }) => {
        state.error = error;
        state.supplierDetailsLoading = false;
      });
  }
});

export const {
  changeQuery,
  changeProductSupplierFiltersQuery,
  clearSuppliers,
  clearSupplierItems,
  selectSupplier,
  setSupplierDetails,
  setSuccess,
  setError
} = customerProductSuppliersSlice.actions;

export default customerProductSuppliersSlice.reducer;
